export interface PageContextPayload {
  sku: string | null
  pathname: string
}

interface AttributeSource {
  getAttribute(name: string): string | null
}

interface TextSource {
  textContent: string | null
}

export interface PageContextDocument {
  querySelector(selector: string): AttributeSource | null
  querySelectorAll(selector: string): ArrayLike<TextSource>
}

const SKU_ATTRIBUTE = "data-product-sku"

export const JSON_LD_TYPE = "application/ld+json"

export const JSON_LD_SELECTOR = `script[type="${JSON_LD_TYPE}"]`

export const OG_SKU_META_PROPERTIES = [
  "product:retailer_item_id",
  "product:sku",
  "og:sku",
]

const OG_SKU_SELECTORS = OG_SKU_META_PROPERTIES.map(
  (property) => `meta[property="${property}"]`,
)

export const PRODUCT_SIGNAL_SELECTOR = [
  JSON_LD_SELECTOR,
  ...OG_SKU_SELECTORS,
].join(", ")

const MAX_JSON_LD_DEPTH = 6

function cleanSku(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function isProductNode(node: Record<string, unknown>): boolean {
  const type = node["@type"]
  if (typeof type === "string") return type === "Product"
  if (Array.isArray(type)) return type.some((t) => t === "Product")
  return false
}

function findJsonLdSku(node: unknown, depth: number): string | null {
  if (depth > MAX_JSON_LD_DEPTH || node === null || typeof node !== "object") {
    return null
  }

  if (Array.isArray(node)) {
    for (const entry of node) {
      const found = findJsonLdSku(entry, depth + 1)
      if (found) return found
    }
    return null
  }

  const record = node as Record<string, unknown>
  if (isProductNode(record)) {
    const sku = cleanSku(record.sku) ?? cleanSku(record.gtin)
    if (sku) return sku
  }

  for (const value of Object.values(record)) {
    const found = findJsonLdSku(value, depth + 1)
    if (found) return found
  }
  return null
}

function detectJsonLdSku(doc: PageContextDocument): string | null {
  const blocks = doc.querySelectorAll(JSON_LD_SELECTOR)
  for (let i = 0; i < blocks.length; i += 1) {
    const raw = blocks[i]?.textContent
    if (!raw) continue
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      continue
    }
    const sku = findJsonLdSku(parsed, 0)
    if (sku) return sku
  }
  return null
}

function detectOpenGraphSku(doc: PageContextDocument): string | null {
  for (const selector of OG_SKU_SELECTORS) {
    const sku = cleanSku(doc.querySelector(selector)?.getAttribute("content"))
    if (sku) return sku
  }
  return null
}

export function detectPageSku(
  doc: PageContextDocument | null,
  bootScript: AttributeSource | null,
): string | null {
  const explicit = cleanSku(bootScript?.getAttribute(SKU_ATTRIBUTE))
  if (explicit) return explicit
  if (!doc) return null
  return detectJsonLdSku(doc) ?? detectOpenGraphSku(doc)
}

export function readPathname(href: string): string {
  try {
    return new URL(href).pathname
  } catch {
    return "/"
  }
}
